import {
  useEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils/style.util";

const modalVariants = cva(
  [
    "relative",
    "bg-atelier-surface-raised",
    "rounded-atelier-xl",
    "shadow-atelier-lg",
    "w-full",
    "flex flex-col",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "max-w-md",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
      },
    },
    defaultVariants: { size: "md" },
  },
);

type ModalVariantsProps = VariantProps<typeof modalVariants>;

type ModalProps = {
  "open": boolean
  "onClose": () => void
  "children": ReactNode
  "aria-label"?: string
  "aria-labelledby"?: string
  "closeOnBackdropClick"?: boolean
  "closeOnEsc"?: boolean
  "className"?: string
} & ModalVariantsProps & Omit<HTMLAttributes<HTMLDivElement>, "aria-label" | "aria-labelledby" | "className" | "children">;

/**
 * Atelier Modal — centered overlay with backdrop, radius-xl, soft shadow.
 *
 * Animations: backdrop fade (180ms), content fade + translateY(4px→0) (180ms).
 * No spring, no scale. Governed by CSS transitions with `data-state` attribute.
 *
 * Closes on Esc or backdrop click (both opt-outable).
 * Focus trap is minimal: we move focus to the content on open.
 */
const Modal = ({
  open,
  onClose,
  size,
  children,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  className,
  ...rest
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !closeOnEsc) {
      return undefined;
    }
    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [open, closeOnEsc, onClose]);

  useEffect(() => {
    if (open) {
      contentRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const body = (
    <div
      role="presentation"
      data-state="open"
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-4",
        "bg-atelier-ink-primary/40 backdrop-blur-[2px]",
        "animate-in fade-in duration-200 ease-out",
      )}
      onClick={(event) => {
        if (!closeOnBackdropClick) {
          return;
        }
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={cn(
          modalVariants({ size }),
          "focus:outline-none",
          "animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return body;
  }
  return createPortal(body, document.body);
};

Modal.displayName = "Atelier.Modal";

export { Modal };
export type { ModalProps };
